'use client'

import React, { useRef, useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ChevronLeft, ChevronRight, Store } from 'lucide-react'
import { getBaseImageUrl, getMerchantHref } from '@/constants/hooks'
import { MerchantWithOffers } from '@/services/dataTypes'

interface Props {
    merchants: MerchantWithOffers[]
    domain: string
    storeSlug?: string
    slugType?: string
}

const FeaturedStoresSlider = ({
    merchants,
    domain,
    storeSlug = 'store',
    slugType = 'slug',
}: Props) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(true)

    const checkScrollButtons = () => {
        if (!scrollContainerRef.current) return
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
        setCanScrollLeft(scrollLeft > 10)
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10)
    }

    useEffect(() => {
        checkScrollButtons()
        const el = scrollContainerRef.current
        if (el) {
            el.addEventListener('scroll', checkScrollButtons, { passive: true })
            window.addEventListener('resize', checkScrollButtons)
        }
        return () => {
            if (el) el.removeEventListener('scroll', checkScrollButtons)
            window.removeEventListener('resize', checkScrollButtons)
        }
    }, [merchants])

    const handleScroll = (direction: 'left' | 'right') => {
        if (!scrollContainerRef.current) return
        const scrollAmount = 360
        scrollContainerRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
        })
    }

    if (!merchants || merchants.length === 0) return null

    return (
        <div className="border-b bg-white border-[#f0ede8]">
            <div className="max-w-7xl mx-auto px-5 py-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-[#2CBBC3]/10 text-[#2CBBC3]">
                            <Store size={15} />
                        </div>
                        <div>
                            <h2 className="text-sm font-black text-gray-900 uppercase tracking-wider">
                                Featured Partner Stores
                            </h2>
                            <p className="text-[11px] text-gray-500 font-medium hidden sm:block">
                                Top brands offering verified discounts for this promotion
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 self-start sm:self-auto">
                        <Link
                            href="/all-stores/A"
                            className="inline-flex items-center gap-1 text-xs font-bold text-[#8C5826] hover:text-[#5e3814] transition-colors"
                        >
                            <span>View All Stores</span>
                            <ChevronRight size={13} />
                        </Link>

                        {/* Slider Navigation Arrows */}
                        <div className="flex items-center gap-1.5 pl-2 border-l border-gray-200">
                            <button
                                type="button"
                                onClick={() => handleScroll('left')}
                                disabled={!canScrollLeft}
                                className="w-8 h-8 rounded-xl border border-[#f0ede8] bg-[#fffaf5] flex items-center justify-center text-gray-600 hover:text-[#8C5826] hover:border-[#8C5826]/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-2xs cursor-pointer active:scale-95"
                                aria-label="Previous stores"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => handleScroll('right')}
                                disabled={!canScrollRight}
                                className="w-8 h-8 rounded-xl border border-[#f0ede8] bg-[#fffaf5] flex items-center justify-center text-gray-600 hover:text-[#8C5826] hover:border-[#8C5826]/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-2xs cursor-pointer active:scale-95"
                                aria-label="Next stores"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Slider Horizontal Container */}
                <div
                    ref={scrollContainerRef}
                    className="flex gap-3.5 overflow-x-auto pb-2 scrollbar-hide scroll-smooth"
                >
                    {merchants.slice(0, 16).map((merchant: MerchantWithOffers, i: number) => {
                        const merchantHref = getMerchantHref(merchant, storeSlug, slugType)
                        const logoUrl = getBaseImageUrl(domain, merchant.merchant_logo, '')
                        const totalOffers = merchant?.offers?.length || 0

                        return (
                            <Link
                                key={i}
                                href={merchantHref}
                                className="group flex items-center gap-3 p-2.5 pr-4 rounded-2xl bg-[#fffaf5] border border-[#f0ede8] hover:border-[#8C5826]/30 hover:shadow-md transition-all shrink-0 hover:-translate-y-0.5"
                            >
                                <div className="w-12 h-12 rounded-xl bg-white border border-[#f0ede8] flex items-center justify-center p-1.5 shrink-0 shadow-2xs group-hover:scale-105 transition-transform">
                                    <Image
                                        src={logoUrl}
                                        alt={merchant.merchant_name || 'Store'}
                                        width={40}
                                        height={40}
                                        className="object-contain w-full h-full"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-black text-gray-900 group-hover:text-[#8C5826] transition-colors truncate max-w-[110px]">
                                        {merchant.merchant_name}
                                    </p>
                                    <p className="text-[10px] font-bold text-[#8C5826]">
                                        {totalOffers > 0 ? `${totalOffers} Deals` : 'View Deals'}
                                    </p>
                                </div>
                                <ChevronRight size={12} className="text-gray-400 group-hover:text-[#8C5826] group-hover:translate-x-0.5 transition-all ml-1 shrink-0" />
                            </Link>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default FeaturedStoresSlider
